import React, { useState, useEffect } from 'react';
import { Phone, MessageSquare, Calendar } from 'lucide-react';
import { businessInfo } from '../mockData';

const Hero = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Photos de mains en gros plan qui massent
  const heroImages = [
    "https://customer-assets.emergentagent.com/job_equicanine-spa/artifacts/wfj5ff2r_IMG_1648.jpeg",
    "https://customer-assets.emergentagent.com/job_equicanine-spa/artifacts/42h7wti3_IMG_1681.jpeg",
    "https://customer-assets.emergentagent.com/job_equicanine-spa/artifacts/noqwm0a9_IMG_1600.jpeg",
    "https://customer-assets.emergentagent.com/job_equicanine-spa/artifacts/93aa4jba_IMG_1563.jpeg",
    "https://customer-assets.emergentagent.com/job_equicanine-spa/artifacts/40avtvuz_IMG_1558.jpeg",
    "https://customer-assets.emergentagent.com/job_equicanine-spa/artifacts/ld9wwaif_IMG_1575.jpeg",
    "https://customer-assets.emergentagent.com/job_equicanine-spa/artifacts/gbe98tyl_IMG_0505.jpeg",
    "https://customer-assets.emergentagent.com/job_equicanine-spa/artifacts/45q8sirq_IMG_0503.jpeg",
    "https://customer-assets.emergentagent.com/job_equicanine-spa/artifacts/y40wtwa3_IMG_0463.jpeg"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 5000); // Change image every 5 seconds

    return () => clearInterval(interval);
  }, [heroImages.length]);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="hero" className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
      {/* Background Image Carousel */}
      <div className="absolute inset-0">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={image}
              alt="Massage animalier professionnel"
              className="w-full h-full object-cover"
            />
            {/* Subtle dark overlay for text readability */}
            <div className="absolute inset-0 bg-black/30"></div>
          </div>
        ))}
      </div>
      
      {/* Decorative golden elements */}
      <div className="absolute top-20 right-20 w-64 h-64 bg-[#C5A572]/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 left-20 w-80 h-80 bg-[#B8860B]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>

      {/* Content */}
      <div className="container mx-auto px-4 py-20 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Logo */}
          <div className="mb-8 flex justify-center animate-fadeIn">
            <img 
              src="https://customer-assets.emergentagent.com/job_equicanine-spa/artifacts/oowyuozl_IMG_2303.png" 
              alt="Gratia Animalis Logo" 
              className="h-32 w-auto drop-shadow-2xl"
            />
          </div>

          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight animate-fadeIn" style={{ animationDelay: '0.2s' }}>
            Reconnecter le corps
            <span className="block text-[#C5A572] mt-2 drop-shadow-lg">Libérer le mouvement</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed animate-fadeIn" style={{ animationDelay: '0.4s' }}>
            Massage professionnel pour chevaux et chiens<br />
            Préparation à l'effort • Récupération • Bien-être
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12 animate-fadeIn" style={{ animationDelay: '0.6s' }}>
            <button
              onClick={scrollToContact}
              className="group bg-gradient-to-r from-[#C5A572] to-[#B8860B] text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl hover:shadow-[#C5A572]/50 transition-all duration-300 hover:scale-105 flex items-center gap-2"
            >
              <Calendar className="group-hover:rotate-12 transition-transform" size={24} />
              Prendre Rendez-vous
            </button>
            
            <a
              href={`tel:${businessInfo.phone}`}
              className="bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-full font-bold text-lg hover:bg-white/20 transition-all duration-300 hover:scale-105 flex items-center gap-2"
            >
              <Phone size={24} />
              Appeler maintenant
            </a>
          </div>

          {/* Quick contact buttons */}
          <div className="flex flex-wrap gap-4 justify-center animate-fadeIn" style={{ animationDelay: '0.8s' }}>
            <a
              href={`sms:${businessInfo.phone}`}
              className="bg-white/10 backdrop-blur-sm text-white px-6 py-3 rounded-full hover:bg-white/20 transition-all duration-300 flex items-center gap-2 border border-white/20"
            >
              <MessageSquare size={20} />
              <span>SMS</span>
            </a>
            <a
              href={businessInfo.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/10 backdrop-blur-sm text-white px-6 py-3 rounded-full hover:bg-white/20 transition-all duration-300 border border-white/20"
            >
              Facebook
            </a>
            <a
              href={businessInfo.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/10 backdrop-blur-sm text-white px-6 py-3 rounded-full hover:bg-white/20 transition-all duration-300 border border-white/20"
            >
              Instagram
            </a>
          </div>

          {/* Zone coverage */}
          <div className="mt-12 inline-block bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl px-6 py-4 animate-fadeIn" style={{ animationDelay: '1s' }}>
            <p className="text-white/90 text-sm mb-1">Zone d'intervention</p>
            <p className="text-[#C5A572] font-semibold text-lg">{businessInfo.coverageArea}</p>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white/50 rounded-full mt-2"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
